import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { Page, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  fmtNOK, fmtDate, CUSTOMER_STATUS, KYC_STATUS, RISK, BILLING, SIGN_STATUS,
} from "@/lib/labels";
import {
  ArrowLeft, Send, Plus, Trash2, FileText, Download, Sparkles, Check, Upload,
  Building2, Mail, ShieldCheck, TrendingUp, Pencil, RefreshCw, AlertTriangle, Users2, Fingerprint, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import TaskFormDialog, { RECURRENCE_LABELS } from "@/components/TaskFormDialog";
import PhoneInput from "@/components/PhoneInput";
import OwnershipTree from "@/components/OwnershipTree";
import ProffFinancials from "@/components/ProffFinancials";
import { YearEndCustomerSection } from "@/components/YearEndChecklist";
import { OppdragCustomerSection } from "@/components/OppdragExecution";

const TABS = [
  ["oversikt", "Oversikt"], ["kontakter", "Kontakter"], ["produkter", "Produkter og priser"],
  ["kyc", "KYC"], ["risiko", "Risiko"], ["avtaler", "Avtaler"], ["varsler", "Varsler"],
  ["nokkeltall", "Nøkkeltall"], ["compliance", "Frister & plikter"], ["dokumenter", "Dokumenter"],
  ["oppgaver", "Oppgaver"], ["oppdragsutforelse", "Oppdragsutførelse"],
  ["aktiviteter", "Aktiviteter"], ["notater", "Interne notater"],
  ["historikk", "Historikk"],
];

function Info({ label, value }) {
  return (
    <div>
      <p className="label-caps">{label}</p>
      <p className="text-sm mt-0.5">{value || "—"}</p>
    </div>
  );
}

function BrregRolesCard({ c, onSync, syncing }) {
  const groups = c.brreg_roles || [];
  return (
    <Card title="Roller (Brønnøysund)" action={<Users2 className="w-4 h-4 text-muted-foreground" />}>
      {groups.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          <p>Ingen roller hentet ennå.</p>
          {c.org_number && (
            <Button size="sm" variant="secondary" className="mt-3" data-testid="brreg-fetch-roles-btn" onClick={onSync} disabled={syncing}>
              <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> Hent styre og signaturer
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="brreg-roles">
          {groups.map((g, gi) => (
            <div key={`${g.type}-${gi}`}>
              <p className="label-caps mb-1">{g.type}</p>
              <div className="space-y-1">
                {g.roles.map((r, ri) => (
                  <div key={`${r.name}-${ri}`} className="flex items-center justify-between text-sm">
                    <span>{r.name}</span>
                    <span className="text-xs text-muted-foreground">{r.role}{r.is_person && r.detail ? ` · f. ${r.detail}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground pt-2 border-t border-border">Kilde: Enhetsregisteret. Brukes til å identifisere signaturberettigede og reelle rettighetshavere i KYC.</p>
        </div>
      )}
    </Card>
  );
}

export default function CustomerCard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("oversikt");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => api.get(`/customers/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const bankid = searchParams.get("bankid");
    if (!bankid) return;
    setTab("kyc");
    if (bankid === "success") {
      const state = searchParams.get("state");
      if (state) {
        api.get(`/bankid/result/${state}`).then(({ data }) => {
          const nm = data?.identity?.name || "person";
          toast.success(`BankID-verifisert: ${nm}`, { description: data?.identity?.national_id ? `Fødselsnummer bekreftet` : undefined });
          load();
        }).catch(() => toast.success("BankID-verifisering fullført"));
      } else toast.success("BankID-verifisering fullført");
    } else {
      toast.error("BankID-verifisering avbrutt", { description: searchParams.get("msg") || undefined });
    }
    searchParams.delete("bankid"); searchParams.delete("state"); searchParams.delete("msg");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, load]);

  const invite = async () => {
    try {
      const { data } = await api.post(`/customers/${id}/invite`);
      const sent = data.email_status === "sent";
      toast.success(sent ? `KYC-invitasjon sendt på e-post til ${data.email}` : `KYC-invitasjon klargjort for ${data.email}`, {
        description: sent ? `Engangskode: ${data.code}` : `E-post ikke sendt (mangler Resend-nøkkel). Lenke: ${data.full_link || data.link} · Kode: ${data.code}`,
        duration: 12000,
      });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (status) => { await api.put(`/customers/${id}`, { status }); load(); };

  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const del = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Kunde arkivert");
      nav("/kunder");
    } catch (e) { toast.error(apiError(e)); setDeleting(false); }
  };

  if (!c) return <Page><p className="text-muted-foreground">Laster…</p></Page>;

  return (
    <Page>
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Tilbake
      </button>

      <div className="bg-card border border-border rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-medium tracking-tight" data-testid="customer-name">{c.legal_name}</h1>
              <StatusBadge map={CUSTOMER_STATUS} value={c.status} />
              {c.risk_level && <StatusBadge map={RISK} value={c.risk_level} />}
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              {c.company_form || "—"} · Org.nr {c.org_number || "—"} · {c.industry || "Ukjent bransje"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="invite-kyc-btn" variant="secondary" onClick={invite}><Send className="w-4 h-4 mr-1" /> Send KYC-invitasjon</Button>
            <Select value={c.status} onValueChange={setStatus}>
              <SelectTrigger className="w-44 bg-secondary border-border" data-testid="status-change"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {Object.entries(CUSTOMER_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={delOpen} onOpenChange={setDelOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" data-testid="delete-customer-btn"><Trash2 className="w-4 h-4 mr-1" /> Arkiver</Button>
              </DialogTrigger>
              <DialogContent className="bg-popover border-border">
                <DialogHeader><DialogTitle>Arkiver kunde</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Er du sikker på at du vil arkivere <strong>{c.legal_name}</strong>? Kunden settes til «avsluttet» og skjules fra aktive lister. All data (KYC, risiko, avtaler, dokumenter, timer, aktiviteter og notater) bevares og kan gjenopprettes ved behov.</p>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setDelOpen(false)}>Avbryt</Button>
                  <Button variant="destructive" onClick={del} disabled={deleting} data-testid="confirm-delete-customer-btn">{deleting ? "Arkiverer…" : "Arkiver kunde"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
          <div><p className="label-caps">MRR</p><p className="font-display text-2xl text-emerald-400">{fmtNOK(c.mrr)}</p></div>
          <div><p className="label-caps">ARR</p><p className="font-display text-2xl">{fmtNOK(c.arr)}</p></div>
          <div><p className="label-caps">KYC-status</p><p className="text-sm mt-1">{KYC_STATUS[c.kyc_status]}</p></div>
          <div><p className="label-caps">Signering</p><p className="text-sm mt-1">{SIGN_STATUS[c.sign_status]}</p></div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full overflow-x-auto no-scrollbar flex justify-start h-auto bg-transparent border-b border-border rounded-none p-0 gap-1">
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}
              className="data-[state=active]:bg-secondary data-[state=active]:text-foreground rounded-md px-3 py-2 text-sm whitespace-nowrap">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="oversikt"><Oversikt c={c} reload={load} /></TabsContent>
        <TabsContent value="kontakter"><Kontakter c={c} reload={load} /></TabsContent>
        <TabsContent value="produkter"><Produkter c={c} reload={load} /></TabsContent>
        <TabsContent value="kyc"><KycTab c={c} reload={load} /></TabsContent>
        <TabsContent value="risiko"><RisikoTab c={c} reload={load} /></TabsContent>
        <TabsContent value="avtaler"><AvtalerTab c={c} reload={load} /></TabsContent>
        <TabsContent value="dokumenter"><DokumenterTab c={c} reload={load} /></TabsContent>
        <TabsContent value="oppgaver"><OppgaverTab c={c} /></TabsContent>
        <TabsContent value="oppdragsutforelse"><div className="mt-2"><OppdragCustomerSection customerId={c.id} showEmpty /></div></TabsContent>
        <TabsContent value="aktiviteter"><AktiviteterTab c={c} /></TabsContent>
        <TabsContent value="notater"><NotaterTab c={c} /></TabsContent>
        <TabsContent value="varsler"><VarslerTab c={c} reload={load} /></TabsContent>
        <TabsContent value="nokkeltall"><NokkeltallTab c={c} /></TabsContent>
        <TabsContent value="compliance"><ComplianceTab c={c} /></TabsContent>
        <TabsContent value="historikk"><HistorikkTab c={c} /></TabsContent>
      </Tabs>
    </Page>
  );
}

const KRR_VARSLING = { KAN_VARSLES: "Kan varsles", KAN_IKKE_VARSLES: "Kan ikke varsles" };
const KRR_STATUS = { AKTIV: "Aktiv", IKKE_REGISTRERT: "Ikke registrert", SLETTET: "Slettet" };

function Card({ title, children, action }) {
  return (
    <div className="bg-card border border-border rounded-xl p-6 mt-6">
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="font-display text-lg">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

const COMPLIANCE_REQ = {
  always: { label: "Alltid", cls: "bg-red-500/15 text-red-400" },
  conditionally: { label: "Betinget", cls: "bg-amber-500/15 text-amber-400" },
  never: { label: "Nei", cls: "bg-zinc-500/15 text-zinc-400" },
};
const COMPLIANCE_FREQ = { annual: "Årlig", monthly: "Månedlig", quarterly: "Kvartalsvis", "one-time": "Engangs" };
function prettyObl(n) {
  const map = { a_melding: "A-melding", mva_filing: "MVA-melding", naeringsoppgave_as: "Næringsoppgave",
    mva_rate_food: "MVA-sats mat", mva_rate_reduced: "MVA-sats redusert", mva_rate_standard: "MVA-sats standard",
    foretaksregister_as: "Registrering i Foretaksregisteret" };
  return map[n] || n;
}

function ComplianceTab({ c }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get(`/customers/${c.id}/compliance`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [c.id]);
  const toggle = async (d, done) => {
    try {
      await api.post(`/customers/${c.id}/compliance/complete`, {
        obligation_id: d.obligation_id, obligation_name: d.obligation_name,
        deadline: d.deadline, period: d.period, done,
      });
      toast.success(done ? "Markert gjennomført" : "Gjenåpnet");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (loading) return <Card title="Frister & plikter"><p className="text-sm text-muted-foreground">Laster…</p></Card>;
  if (data && data.error === "unavailable") {
    return (
      <Card title="Frister & plikter">
        <p className="text-sm text-muted-foreground">Compliance-data er midlertidig utilgjengelig (apier.no svarer ikke). Prøv igjen om litt.</p>
      </Card>
    );
  }
  if (!data || !data.supported) {
    return (
      <Card title="Frister & plikter">
        <p className="text-sm text-muted-foreground">
          Fant ikke støttet selskapsform for automatisk oppslag{data?.company_form ? ` (${data.company_form})` : ""}.
          Støttede: AS, ENK, ANS, DA, NUF. Hent selskapsdata fra Brønnøysund på Oversikt-fanen.
        </p>
      </Card>
    );
  }
  return (
    <div data-testid="customer-compliance">
      <Card title={`Kommende frister (${data.entity_type})`}>
        {data.deadlines.length === 0 ? <p className="text-sm text-muted-foreground">Ingen gjenstående frister i år.</p> : (
          <div className="space-y-2">
            {data.deadlines.map((d) => {
              const done = d.task_status === "ferdig";
              return (
                <div key={d.obligation_id} className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${done ? "border-emerald-500/30 bg-emerald-500/5" : "border-border"}`} data-testid={`cc-deadline-${d.obligation_id}`}>
                  <div>
                    <p className={`text-sm font-medium ${done ? "line-through text-muted-foreground" : ""}`}>{prettyObl(d.obligation_name)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{d.period} · {d.legal_reference}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm whitespace-nowrap">{fmtDate(d.deadline)}</span>
                    {d.tracked && (done ? (
                      <Button size="sm" variant="ghost" data-testid={`cc-reopen-${d.obligation_id}`} onClick={() => toggle(d, false)} className="text-xs text-muted-foreground">Angre</Button>
                    ) : (
                      <Button size="sm" variant="secondary" data-testid={`cc-complete-${d.obligation_id}`} onClick={() => toggle(d, true)} className="text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Gjennomført</Button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">A-melding, MVA-melding, skattemelding og årsregnskap kan markeres gjennomført. Oppgaver opprettes automatisk og vises under Oppgaver.</p>
      </Card>
      <Card title="Plikter for selskapsformen">
        <div className="space-y-2">
          {data.obligations.map((o) => (
            <div key={o.obligation_id} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{prettyObl(o.obligation_name)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{COMPLIANCE_FREQ[o.frequency] || o.frequency}{o.legal_reference ? ` · ${o.legal_reference}` : ""}</p>
                  {o.condition && <p className="text-xs text-muted-foreground mt-1">{o.condition}</p>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${(COMPLIANCE_REQ[o.required] || COMPLIANCE_REQ.never).cls}`}>{(COMPLIANCE_REQ[o.required] || COMPLIANCE_REQ.never).label}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4">Kilde: apier.no (Brønnøysund + Apier Rulebook). Veiledende.</p>
      </Card>
    </div>
  );
}

function VarslerTab({ c, reload }) {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);
  const load = useCallback(() => api.get(`/customers/${c.id}/alerts`).then((r) => setData(r.data)).catch(() => {}), [c.id]);
  useEffect(() => { load(); }, [load]);
  const runCheck = async () => {
    setChecking(true);
    try {
      const { data: res } = await api.post(`/customers/${c.id}/monitor`);
      toast.success(res.change_count ? `${res.change_count} endring(er) oppdaget` : "Ingen endringer siden sist");
      load(); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setChecking(false); }
  };
  const markRead = async (aid) => { await api.post(`/customers/${c.id}/alerts/${aid}/read`); load(); };
  return (
    <Card title="Løpende overvåkning (Brønnøysund)" action={
      <Button size="sm" variant="secondary" data-testid="run-monitor-btn" onClick={runCheck} disabled={checking || !c.org_number}>
        <RefreshCw className={`w-4 h-4 mr-1 ${checking ? "animate-spin" : ""}`} /> {checking ? "Kontrollerer…" : "Kjør kontroll nå"}
      </Button>}>
      <p className="text-xs text-muted-foreground mb-4">
        Sist kontrollert: {data?.monitored_at ? fmtDate(data.monitored_at) : "aldri"}. Overvåker firmanavn, adresse, styre/roller, konkurs og avvikling.
      </p>
      {data?.announcements?.length > 0 && (
        <div className="space-y-2 mb-4">
          {data.announcements.map((a, i) => (
            <div key={i} className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="announcement">
              <AlertTriangle className="w-4 h-4" /> <strong>{a.type}:</strong> {a.text}
            </div>
          ))}
        </div>
      )}
      <p className="label-caps mb-2">Endringsvarsler</p>
      {(!data?.alerts || data.alerts.length === 0) ? (
        <p className="text-sm text-muted-foreground">Ingen registrerte endringer.</p>
      ) : (
        <div className="space-y-2">
          {data.alerts.map((a) => (
            <div key={a.id} className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${a.read ? "border-border bg-secondary/30" : "border-amber-500/30 bg-amber-500/10"}`} data-testid={`alert-${a.id}`}>
              <div>
                <p className="text-sm font-medium">{a.field} <span className="text-xs text-muted-foreground">· {a.type === "kunngjoring" ? "Kunngjøring" : "Endring"}</span></p>
                <p className="text-xs text-muted-foreground">{a.old ? `Fra: ${a.old} → ` : ""}{a.new} · {fmtDate(a.created_at)}</p>
              </div>
              {!a.read && <button onClick={() => markRead(a.id)} className="text-xs text-muted-foreground hover:text-foreground shrink-0">Merk lest</button>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function NokkeltallTab({ c }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback((refresh) => {
    setLoading(true);
    api.get(`/customers/${c.id}/financials${refresh ? "?refresh=true" : ""}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [c.id]);
  useEffect(() => { load(false); }, [load]);
  const nok = (v) => (v == null ? "—" : fmtNOK(v));
  const years = data?.years || [];
  return (
    <>
    <Card title="Nøkkeltall (Regnskapsregisteret)" action={
      <Button size="sm" variant="secondary" data-testid="refresh-financials-btn" onClick={() => load(true)} disabled={loading || !c.org_number}>
        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Hent siste tall
      </Button>}>
      {years.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="financials-empty">{loading ? "Henter…" : "Ingen regnskapstall funnet. Klikk «Hent siste tall»."}</p>
      ) : (
        <table className="w-full text-sm" data-testid="financials-table">
          <thead><tr className="border-b border-border text-left">{["År", "Driftsinntekter", "Driftsresultat", "Årsresultat", "Egenkapital"].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
          <tbody>
            {years.map((y, i) => (
              <tr key={y.to || i} className="border-b border-border/50">
                <td className="px-3 py-2 font-medium">{y.period}</td>
                <td className="px-3 py-2">{nok(y.revenue)}</td>
                <td className="px-3 py-2">{nok(y.operating_result)}</td>
                <td className={`px-3 py-2 ${y.result < 0 ? "text-red-400" : "text-emerald-400"}`}>{nok(y.result)}</td>
                <td className={`px-3 py-2 ${y.equity < 0 ? "text-red-400" : ""}`}>{nok(y.equity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data?.synced_at && <p className="text-xs text-muted-foreground mt-3">Sist hentet: {fmtDate(data.synced_at)} · Kilde: Regnskapsregisteret, Brønnøysund</p>}
    </Card>
    <ProffFinancials customerId={c.id} orgNumber={c.org_number} />
    </>
  );
}

function EditField({ label, value, onChange, type = "text", full, testid }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="label-caps">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-secondary border-border h-9" data-testid={testid} />
    </div>
  );
}

function Oversikt({ c, reload }) {
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [f, setF] = useState({});
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  const syncBrreg = async () => {
    setSyncing(true);
    try {
      await api.post(`/customers/${c.id}/brreg-refresh`);
      toast.success("Firmadata og roller oppdatert fra Brønnøysundregistrene");
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  const startEdit = () => {
    setF({
      legal_name: c.legal_name, org_number: c.org_number, company_form: c.company_form,
      industry: c.industry, industry_code: c.industry_code, founded_date: c.founded_date,
      address: c.address, website: c.website, email: c.email, phone: c.phone,
      vat_registered: !!c.vat_registered,
      has_payroll: c.has_payroll !== false,
      engagement_manager: c.engagement_manager, engagement_manager_name: c.engagement_manager_name,
      associate: c.associate, associate_name: c.associate_name,
      departments: c.departments || (c.department ? [c.department] : []),
      location: c.location, source: c.source, customer_since: c.customer_since,
      billing_type: c.billing_type || "auto",
      monthly_cost: c.monthly_cost ?? "",
      economics: { ...(c.economics || {}) },
    });
    setEdit(true);
  };

  const setEco = (k, v) => setF((p) => ({ ...p, economics: { ...p.economics, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, monthly_cost: f.monthly_cost === "" || f.monthly_cost == null ? null : Number(f.monthly_cost) };
      await api.put(`/customers/${c.id}`, payload);
      toast.success("Kundekortet er oppdatert");
      setEdit(false);
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!edit) {
    return (
      <div>
        <div className="flex justify-end gap-2 mt-6 -mb-2">
          {c.org_number && (
            <Button size="sm" variant="secondary" data-testid="brreg-refresh-btn" onClick={syncBrreg} disabled={syncing}>
              <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Henter…" : "Oppdater fra Brønnøysund"}
            </Button>
          )}
          <Button size="sm" variant="secondary" data-testid="edit-customer-btn" onClick={startEdit}><Pencil className="w-4 h-4 mr-1" /> Rediger</Button>
        </div>
        {(c.brreg_bankrupt || c.brreg_under_liquidation) && (
          <div className="mt-6 flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="brreg-warning">
            <AlertTriangle className="w-4 h-4" />
            {c.brreg_bankrupt ? "Selskapet er registrert konkurs i Enhetsregisteret. " : ""}
            {c.brreg_under_liquidation ? "Selskapet er under avvikling." : ""}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Card title="Selskapsinformasjon">
              <div className="grid grid-cols-2 gap-4">
                <Info label="Juridisk navn" value={c.legal_name} />
                <Info label="Organisasjonsnummer" value={c.org_number} />
                <Info label="Selskapsform" value={c.company_form} />
                <Info label="Næringskode" value={c.industry_code} />
                <Info label="Bransje" value={c.industry} />
                <Info label="Stiftet" value={fmtDate(c.founded_date)} />
                <Info label="Adresse" value={c.address} />
                <Info label="Nettside" value={c.website} />
                <Info label="MVA-registrert" value={c.vat_registered ? "Ja" : "Nei"} />
                <Info label="Har lønn/ansatte" value={c.has_payroll === false ? "Nei" : "Ja"} />
                <Info label="Antall ansatte" value={c.brreg_employees ?? "—"} />
                <Info label="AML-risiko (bransje)" value={c.industry_risk ? ({ lav: "Lav", normal: "Normal", forhoyet: "Forhøyet", hoy: "Høy" }[c.industry_risk.level] || c.industry_risk.level) : "—"} />
                <Info label="E-post" value={c.email} />
                <Info label="Telefon" value={c.phone} />
              </div>
              {c.brreg_synced_at && <p className="text-xs text-muted-foreground mt-4">Sist synkronisert mot Brønnøysund: {fmtDate(c.brreg_synced_at)}</p>}
            </Card>
            <BrregRolesCard c={c} onSync={syncBrreg} syncing={syncing} />
          </div>
          <div>
            <Card title="Interne roller">
              <div className="grid grid-cols-2 gap-4">
                <Info label="Oppdragsansvarlig" value={c.engagement_manager_name} />
                <Info label="Medarbeider" value={c.associate_name} />
                <Info label="Avdeling(er)" value={(c.departments && c.departments.length ? c.departments.join(", ") : c.department) || "—"} />
                <Info label="Lokasjon" value={c.location} />
                <Info label="Kunde siden" value={fmtDate(c.customer_since)} />
                <Info label="Kilde" value={c.source} />
              </div>
            </Card>
            <Card title="Økonomi">
              <div className="grid grid-cols-2 gap-4">
                <Info label="MRR" value={fmtNOK(c.mrr)} />
                <Info label="ARR" value={fmtNOK(c.arr)} />
                <Info label="Kommende MRR" value={fmtNOK(c.coming_mrr)} />
                <Info label="Oppsagt MRR" value={fmtNOK(c.terminated_mrr)} />
                <Info label="Etableringshonorar" value={c.economics?.setup_fee ? fmtNOK(c.economics.setup_fee) : "—"} />
                <Info label="Timepris" value={c.economics?.hourly_rate ? fmtNOK(c.economics.hourly_rate) : "—"} />
                <Info label="Timepris rådgivning" value={c.economics?.advisory_hourly_rate ? fmtNOK(c.economics.advisory_hourly_rate) : "—"} />
                <Info label="Faktureringsintervall" value={c.economics?.billing_interval || "—"} />
                <Info label="Oppstartsdato" value={fmtDate(c.economics?.start_date)} />
                <Info label="Prisreguleringsdato" value={fmtDate(c.economics?.price_adjust_date)} />
                <Info label="Rabatt" value={c.economics?.discount ? `${c.economics.discount}%` : "—"} />
                <Info label="Kredittid (dager)" value={c.economics?.credit_days || "—"} />
                <Info label="Timepris (grunnlag)" value={c.hourly_rate_used ? fmtNOK(c.hourly_rate_used) : "—"} />
                <Info label="Estimert timebehov/mnd" value={c.estimated_hours != null ? `${c.estimated_hours} t` : "—"} />
                <Info label="Prismodell (fakturering)" value={`${c.billing_type_effective === "fastpris" ? "Fastpris" : "Timebasert"}${!c.billing_type || c.billing_type === "auto" ? " (auto)" : ""}`} />
                <Info label="Kostpris/mnd (Utleie)" value={c.monthly_cost ? fmtNOK(c.monthly_cost) : "—"} />
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end gap-2 mt-6 -mb-2">
        <Button size="sm" variant="ghost" data-testid="cancel-edit-btn" onClick={() => setEdit(false)}>Avbryt</Button>
        <Button size="sm" data-testid="save-customer-btn" onClick={save} disabled={saving} className="bg-white text-black hover:bg-white/90">
          <Check className="w-4 h-4 mr-1" /> {saving ? "Lagrer…" : "Lagre"}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Selskapsinformasjon">
          <div className="grid grid-cols-2 gap-4">
            <EditField label="Juridisk navn" value={f.legal_name} onChange={(v) => setF({ ...f, legal_name: v })} full />
            <EditField label="Organisasjonsnummer" value={f.org_number} onChange={(v) => setF({ ...f, org_number: v })} />
            <EditField label="Selskapsform" value={f.company_form} onChange={(v) => setF({ ...f, company_form: v })} />
            <EditField label="Næringskode" value={f.industry_code} onChange={(v) => setF({ ...f, industry_code: v })} />
            <EditField label="Bransje" value={f.industry} onChange={(v) => setF({ ...f, industry: v })} />
            <EditField label="Stiftet" value={f.founded_date} onChange={(v) => setF({ ...f, founded_date: v })} type="date" />
            <EditField label="Nettside" value={f.website} onChange={(v) => setF({ ...f, website: v })} />
            <EditField label="Adresse" value={f.address} onChange={(v) => setF({ ...f, address: v })} full />
            <EditField label="E-post" value={f.email} onChange={(v) => setF({ ...f, email: v })} />
            <div><Label className="label-caps">Telefon</Label><div className="mt-1.5"><PhoneInput value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="edit-customer-phone" /></div></div>
            <label className="col-span-2 flex items-center gap-2 text-sm mt-1">
              <Checkbox checked={f.vat_registered} onCheckedChange={(v) => setF({ ...f, vat_registered: v })} /> MVA-registrert
            </label>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <Checkbox data-testid="edit-has-payroll" checked={f.has_payroll} onCheckedChange={(v) => setF({ ...f, has_payroll: v })} /> Har lønn/ansatte (styrer a-melding-varsler)
            </label>
          </div>
        </Card>
        <div>
          <Card title="Interne roller">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="label-caps">Oppdragsansvarlig</Label>
                <Select value={f.engagement_manager || ""} onValueChange={(v) => {
                  const u = users.find((x) => x.id === v);
                  setF({ ...f, engagement_manager: v, engagement_manager_name: u ? u.name : "" });
                }}>
                  <SelectTrigger className="mt-1 bg-secondary border-border h-9" data-testid="engagement-manager-select"><SelectValue placeholder="Velg ansatt" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="label-caps">Medarbeider</Label>
                <Select value={f.associate || ""} onValueChange={(v) => {
                  const u = users.find((x) => x.id === v);
                  setF({ ...f, associate: v, associate_name: u ? u.name : "" });
                }}>
                  <SelectTrigger className="mt-1 bg-secondary border-border h-9" data-testid="associate-select"><SelectValue placeholder="Velg medarbeider" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="label-caps">Avdeling(er)</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {departments.map((d) => (
                    <label key={d} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={(f.departments || []).includes(d)}
                        onCheckedChange={() => setF((p) => ({ ...p, departments: (p.departments || []).includes(d) ? p.departments.filter((x) => x !== d) : [...(p.departments || []), d] }))}
                        data-testid={`edit-dept-${d}`}
                      /> {d}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-caps">Lokasjon</Label>
                <Select value={f.location || ""} onValueChange={(v) => setF({ ...f, location: v })}>
                  <SelectTrigger className="mt-1 bg-secondary border-border h-9" data-testid="edit-location-select"><SelectValue placeholder="Velg" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{locations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <EditField label="Kilde" value={f.source} onChange={(v) => setF({ ...f, source: v })} />
              <EditField label="Kunde siden" value={f.customer_since ? f.customer_since.slice(0, 10) : ""} onChange={(v) => setF({ ...f, customer_since: v })} type="date" />
            </div>
          </Card>
          <Card title="Økonomi">
            <div className="grid grid-cols-2 gap-4">
              <EditField label="Etableringshonorar" value={f.economics.setup_fee} onChange={(v) => setEco("setup_fee", v)} type="number" />
              <EditField label="Timepris" value={f.economics.hourly_rate} onChange={(v) => setEco("hourly_rate", v)} type="number" />
              <EditField label="Timepris rådgivning (kr/t)" value={f.economics.advisory_hourly_rate} onChange={(v) => setEco("advisory_hourly_rate", v)} type="number" testid="edit-advisory-rate" />
              <div>
                <Label className="label-caps">Faktureringsintervall</Label>
                <Select value={f.economics.billing_interval || ""} onValueChange={(v) => setEco("billing_interval", v)}>
                  <SelectTrigger className="mt-1 bg-secondary border-border h-9"><SelectValue placeholder="Velg" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{Object.entries(BILLING).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <EditField label="Oppstartsdato" value={f.economics.start_date} onChange={(v) => setEco("start_date", v)} type="date" />
              <EditField label="Prisreguleringsdato" value={f.economics.price_adjust_date} onChange={(v) => setEco("price_adjust_date", v)} type="date" />
              <EditField label="Rabatt (%)" value={f.economics.discount} onChange={(v) => setEco("discount", Math.max(0, Math.min(100, Number(v) || 0)))} type="number" />
              <EditField label="Kredittid (dager)" value={f.economics.credit_days} onChange={(v) => setEco("credit_days", v)} type="number" />
              <div className="col-span-2">
                <Label className="label-caps">Prismodell (fakturering)</Label>
                <Select value={f.billing_type || "auto"} onValueChange={(v) => setF({ ...f, billing_type: v })}>
                  <SelectTrigger className="mt-1 bg-secondary border-border h-9" data-testid="edit-billing-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="auto">Automatisk (utledes fra produkter)</SelectItem>
                    <SelectItem value="timebasert">Timebasert (timer danner fakturagrunnlag)</SelectItem>
                    <SelectItem value="fastpris">Fastpris (timer registreres, ikke fakturagrunnlag)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="col-span-2 text-xs text-muted-foreground">Fastpris: timer føres for kapasitet/rapport, men inngår ikke i timebasert fakturagrunnlag. «Automatisk» = timebasert hvis kunden har minst ett timepris-produkt, ellers fastpris.</p>
              <div className="col-span-2">
                <Label className="label-caps">Kostpris/mnd (kr) – Utleie</Label>
                <Input type="number" step="500" placeholder="F.eks. 18000" value={f.monthly_cost} onChange={(e) => setF({ ...f, monthly_cost: e.target.value })} className="mt-1 bg-secondary border-border h-9" data-testid="edit-monthly-cost" />
                <p className="text-xs text-muted-foreground mt-1">Månedlig kostpris for utleid ressurs. Brukes til reell margin (fastpris − kostpris) i lønnsomhetsrapporten for Utleie-kunder.</p>
              </div>
              <p className="col-span-2 text-xs text-muted-foreground">MRR/ARR beregnes automatisk fra produktene under «Produkter og priser».</p>
              <p className="col-span-2 text-xs text-muted-foreground">Timepris rådgivning brukes som sats for timeføringer med aktivitet «Rådgivning». Er den tom, brukes vanlig timepris (eller foretakets standardpris).</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function splitName(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first_name: parts[0] || "", last_name: "" };
  return { first_name: parts.slice(0, -1).join(" "), last_name: parts[parts.length - 1] };
}

function ContactDialog({ trigger, initial, onSave, title }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(initial); }, [open, initial]);
  const ssnBad = f.ssn && !/^\d{11}$/.test(f.ssn);
  const nameOk = (f.first_name || "").trim() || (f.last_name || "").trim();
  const save = async () => {
    if (!nameOk) { toast.error("Fornavn eller etternavn er påkrevd"); return; }
    if (ssnBad) { toast.error("Fødselsnummer må være 11 siffer"); return; }
    setSaving(true);
    try { await onSave(f); setOpen(false); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="label-caps">Fornavn</Label><Input placeholder="Fornavn" data-testid="contact-first-name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
            <div><Label className="label-caps">Etternavn</Label><Input placeholder="Etternavn" data-testid="contact-last-name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
          </div>
          <Input placeholder="Rolle (f.eks. Daglig leder)" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="bg-secondary border-border" />
          <div>
            <Input placeholder="Fødselsnummer (11 siffer)" data-testid="contact-ssn" value={f.ssn} maxLength={11} inputMode="numeric" onChange={(e) => setF({ ...f, ssn: e.target.value.replace(/\D/g, "") })} className={`bg-secondary border-border ${ssnBad ? "border-destructive" : ""}`} />
            {ssnBad && <p className="text-xs text-destructive mt-1">Fødselsnummer må være 11 siffer</p>}
          </div>
          <Input placeholder="E-post" data-testid="contact-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-secondary border-border" />
          <PhoneInput testid="contact-phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} />
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.primary} onCheckedChange={(v) => setF({ ...f, primary: v })} /> Primærkontakt</label>
          <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="contact-signatory" checked={f.signatory} onCheckedChange={(v) => setF({ ...f, signatory: v })} /> Signaturberettiget</label>
        </div>
        <DialogFooter><Button data-testid="contact-save" onClick={save} disabled={saving} className="bg-white text-black hover:bg-white/90">{saving ? "Lagrer…" : "Lagre"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kontakter({ c, reload }) {
  const [verifying, setVerifying] = useState(null);
  const emptyForm = { first_name: "", last_name: "", role: "", ssn: "", email: "", phone: "", primary: false, signatory: false };
  const addContact = async (f) => { await api.post(`/customers/${c.id}/contacts`, f); toast.success("Kontakt lagt til"); reload(); };
  const editContact = async (id, f) => { await api.put(`/customers/${c.id}/contacts/${id}`, f); toast.success("Kontakt oppdatert"); reload(); };
  const del = async (cid) => { await api.delete(`/customers/${c.id}/contacts/${cid}`); reload(); };
  const verifyKrr = async (ct) => {
    setVerifying(ct.id);
    try { await api.post(`/customers/${c.id}/contacts/${ct.id}/verify-krr`); toast.success("Kontaktinfo hentet fra KRR"); reload(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setVerifying(null); }
  };
  const signatories = (c.contacts || []).filter((x) => x.signatory);
  return (
    <Card title="Kontaktpersoner" action={
      <ContactDialog title="Ny kontaktperson" initial={emptyForm} onSave={addContact}
        trigger={<Button size="sm" data-testid="add-contact-btn" className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Legg til</Button>} />
    }>
      {signatories.length > 1 && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300" data-testid="multi-signatory-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>Denne kunden har <strong>{signatories.length} signaturberettigede</strong> ({signatories.map((s) => s.name).join(", ")}). Oppdragsavtalen krever signatur fra <strong>alle {signatories.length}</strong> før den er gyldig.</div>
        </div>
      )}
      <div className="space-y-2">
        {(c.contacts || []).map((ct) => (
          <div key={ct.id} className="p-3 rounded-lg bg-secondary/50 border border-border" data-testid={`contact-${ct.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">{ct.name} {ct.primary && <span className="text-xs text-blue-400">· Primær</span>} {ct.signatory && <span className="text-xs text-emerald-400">· Signatar</span>}</p>
                <p className="text-xs text-muted-foreground">{[ct.role, ct.ssn && `f.nr ${ct.ssn}`, ct.email, ct.phone].filter(Boolean).join(" · ")}</p>
              </div>
              <div className="flex items-center gap-1">
                {ct.ssn && /^\d{11}$/.test(ct.ssn) && (
                  <Button size="sm" variant="secondary" data-testid={`verify-krr-${ct.id}`} onClick={() => verifyKrr(ct)} disabled={verifying === ct.id} className="text-xs h-8">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {verifying === ct.id ? "Henter…" : "Verifiser i KRR"}
                  </Button>
                )}
                <ContactDialog title="Rediger kontaktperson"
                  initial={{ first_name: ct.first_name || splitName(ct.name).first_name, last_name: ct.last_name || splitName(ct.name).last_name, role: ct.role || "", ssn: ct.ssn || "", email: ct.email || "", phone: ct.phone || "", primary: !!ct.primary, signatory: !!ct.signatory }}
                  onSave={(f) => editContact(ct.id, f)}
                  trigger={<button data-testid={`edit-contact-${ct.id}`} className="text-muted-foreground hover:text-foreground p-2"><Pencil className="w-4 h-4" /></button>} />
                <button data-testid={`delete-contact-${ct.id}`} onClick={() => del(ct.id)} className="text-muted-foreground hover:text-destructive p-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <KrrBox krr={ct.krr} testid={`krr-info-${ct.id}`} />
          </div>
        ))}
        {(!c.contacts || c.contacts.length === 0) && <p className="text-sm text-muted-foreground">Ingen kontaktpersoner registrert.</p>}
      </div>
    </Card>
  );
}

const UNIT_OPTS = [["mnd", "per måned"], ["time", "per time"], ["stk", "per stk"], ["bilag", "per bilag"], ["ar", "per år"], ["fast", "fastpris"]];
const UNIT_LABEL = Object.fromEntries(UNIT_OPTS);

const NO_AREA = "Uten område";

function Produkter({ c, reload }) {
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState("");
  const areas = (c.departments && c.departments.length) ? c.departments : (c.department ? [c.department] : []);
  const multiArea = areas.length >= 2;
  const [override, setOverride] = useState({ price: "", quantity: 1, discount: 0, unit: "mnd" });
  useEffect(() => { api.get("/products").then((r) => setCatalog(r.data)); }, []);
  const availableCatalog = catalog.filter((p) => p.area && areas.includes(p.area));
  const lineTotal = (p) => (Number(p.price) || 0) * (Number(p.quantity) || 1) * (1 - (Number(p.discount) || 0) / 100);
  const add = async () => {
    const p = availableCatalog.find((x) => x.id === sel);
    if (!p) return;
    await api.post(`/customers/${c.id}/products`, {
      product_id: p.id, name: p.name, price: Number(override.price) || p.price,
      billing_interval: p.billing_interval, pricing_model: p.pricing_model,
      quantity: Number(override.quantity) || 1,
      discount: Math.max(0, Math.min(100, Number(override.discount) || 0)),
      unit: override.unit, status: "aktiv", area: p.area,
    });
    setOpen(false); setSel(""); setOverride({ price: "", quantity: 1, discount: 0, unit: "mnd" });
    reload(); toast.success("Produkt lagt til – MRR oppdatert");
  };
  const del = async (pid) => { await api.delete(`/customers/${c.id}/products/${pid}`); reload(); };

  const groupFor = (p) => (p.area && areas.includes(p.area)) ? p.area : NO_AREA;
  const products = c.products || [];
  const mrrByArea = c.mrr_by_area || {};
  const groupKeys = [...areas, ...(products.some((p) => groupFor(p) === NO_AREA) ? [NO_AREA] : [])];

  const ProductTable = ({ rows }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left">{["Produkt", "Prismodell", "Fakturering", "Enhet", "Antall", "Pris", "Rabatt", "Sum", ""].map((h, i) => <th key={i} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-b border-border/50" data-testid={`product-row-${p.id}`}>
              <td className="px-3 py-2 font-medium">{p.name}</td>
              <td className="px-3 py-2 text-muted-foreground">{p.pricing_model}</td>
              <td className="px-3 py-2 text-muted-foreground">{BILLING[p.billing_interval]}</td>
              <td className="px-3 py-2 text-muted-foreground">{UNIT_LABEL[p.unit] || p.unit || "—"}</td>
              <td className="px-3 py-2">{p.quantity || 1}</td>
              <td className="px-3 py-2">{fmtNOK(p.price)}</td>
              <td className="px-3 py-2">{p.discount || 0}%</td>
              <td className="px-3 py-2 font-medium" data-testid={`product-linetotal-${p.id}`}>{fmtNOK(lineTotal(p))}</td>
              <td className="px-3 py-2"><button data-testid={`delete-product-${p.id}`} onClick={() => del(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Card title="Produkter og priser" action={
      areas.length === 0 ? null : (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button size="sm" data-testid="add-product-btn" className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Legg til produkt</Button></DialogTrigger>
        <DialogContent className="bg-popover border-border">
          <DialogHeader><DialogTitle>Legg til produkt</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {availableCatalog.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="no-products-for-area">Ingen produkter i katalogen for kundens område ({areas.join(", ")}). Opprett produkter under riktig område i «Produkter og priser».</p>
            ) : (<>
            <Select value={sel} onValueChange={(v) => { setSel(v); const p = availableCatalog.find((x) => x.id === v); setOverride((o) => ({ ...o, price: p?.price || "", unit: p?.unit || o.unit })); }}>
              <SelectTrigger data-testid="product-select" className="bg-secondary border-border"><SelectValue placeholder="Velg produkt" /></SelectTrigger>
              <SelectContent className="bg-popover border-border max-h-72">
                {availableCatalog.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.area} · {fmtNOK(p.price)}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="label-caps">Pris</Label><Input type="number" data-testid="product-price" value={override.price} onChange={(e) => setOverride({ ...override, price: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
              <div>
                <Label className="label-caps">Enhet</Label>
                <Select value={override.unit} onValueChange={(v) => setOverride({ ...override, unit: v })}>
                  <SelectTrigger data-testid="product-unit" className="mt-1 bg-secondary border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{UNIT_OPTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-caps">Antall</Label><Input type="number" data-testid="product-quantity" value={override.quantity} onChange={(e) => setOverride({ ...override, quantity: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
              <div><Label className="label-caps">Rabatt %</Label><Input type="number" data-testid="product-discount" min={0} max={100} value={override.discount} onChange={(e) => setOverride({ ...override, discount: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} className="mt-1 bg-secondary border-border" /></div>
            </div>
            </>)}
          </div>
          <DialogFooter><Button data-testid="product-add-save" onClick={add} disabled={!sel} className="bg-white text-black hover:bg-white/90">Legg til</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      )
    }>
      {areas.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="no-area-hint">Kunden har ingen forretningsområde ennå. Sett område(r) på kunden i <strong>Oversikt</strong>-fanen for å kunne legge til produkter.</p>
      ) : products.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ingen produkter valgt.</p>
      ) : multiArea ? (
        <div className="space-y-6">
          {groupKeys.map((key) => {
            const rows = products.filter((p) => groupFor(p) === key);
            if (rows.length === 0) return null;
            return (
              <div key={key} data-testid={`product-area-group-${key}`}>
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-display text-base">{key}</h4>
                  <span className="text-sm text-muted-foreground">Sum MRR: <span className="text-emerald-400 font-medium">{fmtNOK(mrrByArea[key] || 0)}</span></span>
                </div>
                <ProductTable rows={rows} />
              </div>
            );
          })}
        </div>
      ) : (
        <ProductTable rows={products} />
      )}
      {products.length > 0 && (
        <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-border">
          <div><p className="label-caps">Sum MRR</p><p className="font-display text-xl text-emerald-400">{fmtNOK(c.mrr)}</p></div>
          <div><p className="label-caps">Sum ARR</p><p className="font-display text-xl">{fmtNOK(c.arr)}</p></div>
          {multiArea && groupKeys.filter((k) => (mrrByArea[k] || 0) > 0).map((k) => (
            <div key={k}><p className="label-caps">{k}</p><p className="font-display text-xl">{fmtNOK(mrrByArea[k] || 0)}</p></div>
          ))}
        </div>
      )}
    </Card>
  );
}

function KrrBox({ krr, testid }) {
  if (!krr) return null;
  return (
    <div className="mt-2 pt-2 border-t border-border/60 text-xs space-y-0.5" data-testid={testid}>
      <p className="label-caps text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-400" /> KRR (Kontakt- og reservasjonsregisteret)</p>
      {krr.status === "IKKE_REGISTRERT" ? (
        <p className="text-muted-foreground">Ikke registrert i KRR.</p>
      ) : (
        <>
          <p><span className="text-muted-foreground">E-post:</span> {krr.epost || "—"}</p>
          <p><span className="text-muted-foreground">Mobil:</span> {krr.mobil || "—"}</p>
          <p><span className="text-muted-foreground">Status:</span> {KRR_VARSLING[krr.varslingsstatus] || krr.varslingsstatus || krr.status}{krr.reservasjon === "JA" ? " · Reservert mot digital kommunikasjon" : ""}{krr.digital_postkasse ? " · Har digital postkasse" : ""}</p>
        </>
      )}
      <p className="text-muted-foreground/70">Verifisert {fmtDate(krr.checked_at)}{krr.checked_by ? ` av ${krr.checked_by}` : ""}</p>
    </div>
  );
}

function ScreeningBox({ s, testid }) {
  if (!s) return null;
  if (s.available === false) {
    return <p className="text-xs text-muted-foreground/60 mt-2" data-testid={testid}>PEP-/sanksjonssjekk utilgjengelig</p>;
  }
  const hasHit = s.pep || s.sanction;
  return (
    <div className="mt-2 pt-2 border-t border-border/60 space-y-1.5" data-testid={testid}>
      <p className="label-caps text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-foreground/70" /> PEP & sanksjoner (Proff)</p>
      <div className="flex flex-wrap items-center gap-2">
        {s.pep
          ? <span className="text-[11px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30" data-testid={`${testid}-pep`}>PEP-treff ({s.pep_count})</span>
          : <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300/90 border border-emerald-500/20">Ingen PEP-treff</span>}
        {s.sanction
          ? <span className="text-[11px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30" data-testid={`${testid}-sanction`}>Sanksjonstreff ({s.sanction_count})</span>
          : <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300/90 border border-emerald-500/20">Ingen sanksjonstreff</span>}
        {s.checked_at && <span className="text-[10px] text-muted-foreground/60">sjekket {fmtDate(s.checked_at)}</span>}
      </div>
      {hasHit && (s.hits || []).length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Vis {s.hits.length} treff for gjennomgang</summary>
          <div className="mt-1 space-y-1.5">
            {s.hits.map((h, i) => (
              <div key={i} className="p-2 rounded bg-red-500/5 border border-red-500/20">
                <p className="text-[11px] font-medium text-red-300">{[h.type, h.list, h.country].filter(Boolean).join(" · ")}</p>
                {h.name && <p className="text-[11px]">{h.name}</p>}
                {h.description && <p className="text-[11px] text-muted-foreground">{h.description}</p>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function KycPersonList({ group, people, meta, verifying, onVerify }) {
  if (!people || people.length === 0) return <p className="text-sm text-muted-foreground">Ingen registrert.</p>;
  return (
    <div className="space-y-2">
      {people.map((o, i) => {
        const key = `${group}-${i}`;
        const validSsn = o.ssn && /^\d{11}$/.test(String(o.ssn).replace(/\D/g, ""));
        return (
          <div key={o._key || key} className="p-3 rounded-lg bg-secondary/50 border border-border" data-testid={`kyc-person-${key}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">{o.name || "—"}{o.is_rrr ? <span className="text-xs text-emerald-400"> · Reell rettighetshaver</span> : null}{o.is_company ? <span className="text-xs text-sky-400"> · Selskap</span> : null}{o.pep ? <span className="text-xs text-amber-400"> · PEP</span> : null}</p>
                <p className="text-xs text-muted-foreground">{[meta(o), o.is_company ? (o.org_number && `org.nr ${o.org_number}`) : (o.ssn && `f.nr ${o.ssn}`)].filter(Boolean).join(" · ")}</p>
              </div>
              {validSsn && (
                <Button size="sm" variant="secondary" data-testid={`kyc-verify-krr-${key}`} onClick={() => onVerify(group, i)} disabled={verifying === key} className="text-xs h-8 shrink-0">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {verifying === key ? "Henter…" : "Verifiser i KRR"}
                </Button>
              )}
            </div>
            <KrrBox krr={o.krr} testid={`kyc-krr-info-${key}`} />
            {group === "beneficial_owners" && <ScreeningBox s={o.screening} testid={`kyc-screening-${key}`} />}
          </div>
        );
      })}
    </div>
  );
}


async function streamGeneration(path, onToken) {
  const token = localStorage.getItem("murphy_token");
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok || !res.body) throw new Error("Nettverksfeil ved AI-generering");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "", full = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const part of parts) {
      const line = part.split("\n").find((l) => l.startsWith("data:"));
      if (!line) continue;
      const evt = JSON.parse(line.slice(5).trim());
      if (evt.type === "token") { full += evt.text; onToken(full); }
      else if (evt.type === "error") { throw new Error(evt.message || "AI-tjeneste utilgjengelig"); }
    }
  }
  return full;
}

function KycAnswersEditor({ questions, initial, onSave, onCancel, saving }) {
  const [a, setA] = useState(initial || {});
  const set = (id, v) => setA((p) => ({ ...p, [id]: v }));
  const sections = [];
  const bySec = {};
  questions.forEach((q) => {
    if (!bySec[q.section]) { bySec[q.section] = []; sections.push(q.section); }
    bySec[q.section].push(q);
  });
  return (
    <div className="space-y-5 mt-1" data-testid="kyc-answers-editor">
      <p className="text-xs text-muted-foreground">Registrer eller korriger kundens KYC-svar internt. Svarene tas med i den AI-genererte kundekontrollrapporten.</p>
      {sections.map((sec) => (
        <div key={sec}>
          <p className="label-caps mb-2">{sec}</p>
          <div className="space-y-3">
            {bySec[sec].map((q) => (
              <div key={q.id}>
                <Label className="text-sm">{q.label}{q.required && <span className="text-destructive"> *</span>}</Label>
                {q.type === "textarea" ? (
                  <Textarea data-testid={`kyc-edit-${q.id}`} value={a[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} rows={2} className="mt-1 bg-secondary border-border" />
                ) : q.type === "select" ? (
                  <Select value={a[q.id] || ""} onValueChange={(v) => set(q.id, v)}>
                    <SelectTrigger data-testid={`kyc-edit-${q.id}`} className="mt-1 bg-secondary border-border"><SelectValue placeholder="Velg…" /></SelectTrigger>
                    <SelectContent>{(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={q.type === "number" ? "number" : "text"} data-testid={`kyc-edit-${q.id}`} value={a[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} className="mt-1 bg-secondary border-border" />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" data-testid="kyc-save-answers" onClick={() => onSave(a)} disabled={saving} className="bg-white text-black hover:bg-white/90">{saving ? "Lagrer…" : "Lagre svar"}</Button>
        <Button size="sm" variant="ghost" data-testid="kyc-cancel-answers" onClick={onCancel} disabled={saving}>Avbryt</Button>
      </div>
    </div>
  );
}

function KycTab({ c, reload }) {
  const [kyc, setKyc] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showAnswers, setShowAnswers] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [fetchingRrr, setFetchingRrr] = useState(false);
  const [genPdf, setGenPdf] = useState(false);
  const [screeningOwners, setScreeningOwners] = useState(false);
  const [editAnswers, setEditAnswers] = useState(false);
  const [savingAnswers, setSavingAnswers] = useState(false);
  const load = useCallback(() => api.get(`/customers/${c.id}/kyc`).then((r) => setKyc(r.data)), [c.id]);
  useEffect(() => { load(); api.get("/kyc/questions").then((r) => setQuestions(r.data)); }, [load]);
  const decide = async (decision) => {
    await api.post(`/customers/${c.id}/kyc/decision`, { decision });
    toast.success(`KYC ${decision}`); load(); reload();
  };
  const startRenewal = async () => {
    await api.post(`/customers/${c.id}/kyc/start-renewal`);
    toast.success("Ny KYC-kontroll startet"); load(); reload();
  };
  const verifyKrr = async (group, index) => {
    setVerifying(`${group}-${index}`);
    try { await api.post(`/customers/${c.id}/kyc/verify-krr`, { group, index }); toast.success("Kontaktinfo hentet fra KRR"); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setVerifying(null); }
  };
  const verifyAllKrr = async () => {
    setVerifyingAll(true);
    try {
      const { data } = await api.post(`/customers/${c.id}/kyc/verify-krr-all`);
      if (data.verified === 0 && data.skipped > 0) toast.info(`Ingen personer med gyldig fødselsnummer å verifisere (${data.skipped} hoppet over)`);
      else toast.success(`Verifiserte ${data.verified} person(er) mot KRR${data.skipped ? `, ${data.skipped} uten fnr` : ""}${data.failed ? `, ${data.failed} feilet` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setVerifyingAll(false); }
  };
  const fetchRrr = async () => {
    setFetchingRrr(true);
    try { const { data } = await api.post(`/customers/${c.id}/kyc/fetch-rrr`, { merge: true }); toast.success(`Hentet ${data.fetched} reelle rettighetshavere fra Proff${data.flagged ? ` · ${data.flagged} PEP/sanksjonstreff` : ""}`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setFetchingRrr(false); }
  };
  const screenOwners = async () => {
    setScreeningOwners(true);
    try { const { data } = await api.post(`/customers/${c.id}/kyc/screen-owners`); toast.success(`Sjekket ${data.screened} person(er) — ${data.flagged} PEP/sanksjonstreff`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setScreeningOwners(false); }
  };
  const genKycPdf = async () => {
    setGenPdf(true);
    try { const { data } = await api.post(`/customers/${c.id}/kyc/generate-pdf`); toast.success(`KYC-rapport generert: ${data.name}. Ligger under fanen «Dokumenter».`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setGenPdf(false); }
  };
  const saveAnswers = async (answers) => {
    setSavingAnswers(true);
    try {
      await api.put(`/customers/${c.id}/kyc`, { answers });
      toast.success("KYC-svar lagret");
      setEditAnswers(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingAnswers(false); }
  };
  const [fetchingBoard, setFetchingBoard] = useState(false);
  const [togglingContact, setTogglingContact] = useState(null);
  const [bankidVerifs, setBankidVerifs] = useState([]);
  const [startingBankid, setStartingBankid] = useState(false);
  const loadBankid = useCallback(() => api.get(`/bankid/verifications/${c.id}`).then((r) => setBankidVerifs(r.data)).catch(() => {}), [c.id]);
  useEffect(() => { loadBankid(); }, [loadBankid, kyc]);
  const startBankid = async () => {
    setStartingBankid(true);
    try {
      const { data } = await api.get(`/bankid/start`, { params: { customer_id: c.id, return_to: `/kunder/${c.id}` } });
      window.location.href = data.authorize_url;
    } catch (e) { toast.error(apiError(e)); setStartingBankid(false); }
  };
  const fetchBoard = async () => {
    setFetchingBoard(true);
    try { const { data } = await api.post(`/customers/${c.id}/kyc/fetch-board`); toast.success(`Hentet ${(data.board_members || []).length} personer fra Brønnøysund`); setKyc(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setFetchingBoard(false); }
  };
  const toggleContact = async (index, add) => {
    setTogglingContact(index);
    try { const { data } = await api.post(`/customers/${c.id}/kyc/board/${index}/toggle-contact`, { add }); toast.success(add ? "Lagt til som kontakt" : "Fjernet fra kontakter"); setKyc(data); reload(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setTogglingContact(null); }
  };
  if (!kyc) return <Card><p className="text-muted-foreground">Laster…</p></Card>;
  const lastC = kyc.last_control || c.last_control;
  const nextC = kyc.next_control || c.next_control;
  return (
    <Card title="KYC / Kundekontroll" action={<StatusBadge map={KYC_STATUS} value={kyc.status} />}>
      {(lastC || nextC) && (
        <div className="flex flex-wrap items-center gap-6 mb-4 p-3 rounded-lg bg-secondary/50 border border-border">
          <div><p className="label-caps">Sist kontrollert</p><p className="text-sm mt-0.5">{fmtDate(lastC)}</p></div>
          <div><p className="label-caps">Neste kontroll</p><p className="text-sm mt-0.5">{fmtDate(nextC)}</p></div>
          <Button size="sm" variant="secondary" data-testid="start-renewal-btn" onClick={startRenewal} className="ml-auto">Start ny kontroll</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button size="sm" data-testid="kyc-approve" onClick={() => decide("godkjent")} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" /> Godkjenn</Button>
        <Button size="sm" variant="secondary" onClick={() => decide("mangler_info")}>Mangler info</Button>
        <Button size="sm" variant="secondary" onClick={() => decide("forsterkede_tiltak")}>Forsterkede tiltak</Button>
        <Button size="sm" data-testid="kyc-reject" variant="destructive" onClick={() => decide("avvist")}>Avvis</Button>
        <Button size="sm" variant="secondary" data-testid="kyc-verify-all-krr" onClick={verifyAllKrr} disabled={verifyingAll} className="ml-auto">
          <ShieldCheck className="w-4 h-4 mr-1" /> {verifyingAll ? "Verifiserer…" : "Verifiser alle i KRR"}
        </Button>
      </div>

      <div className="mb-4 p-4 rounded-lg bg-secondary/50 border border-border" data-testid="bankid-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5 text-foreground/80" />
            <div>
              <p className="text-sm font-medium">BankID-identitetsverifisering</p>
              <p className="text-xs text-muted-foreground">Bekreft en persons identitet med norsk BankID (verifisert navn og fødselsnummer).</p>
            </div>
          </div>
          <Button size="sm" data-testid="bankid-start-btn" onClick={startBankid} disabled={startingBankid} className="bg-white text-black hover:bg-white/90">
            <Fingerprint className="w-4 h-4 mr-1" /> {startingBankid ? "Åpner BankID…" : "Verifiser med BankID"}
          </Button>
        </div>
        {bankidVerifs.length > 0 && (
          <div className="mt-3 space-y-1.5 border-t border-border/60 pt-3" data-testid="bankid-verifications">
            {bankidVerifs.map((v) => (
              <div key={v.id} className="flex items-center gap-2 text-sm" data-testid={`bankid-verif-${v.id}`}>
                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="font-medium">{v.identity?.name || "—"}</span>
                <span className="text-xs text-muted-foreground">
                  {v.identity?.national_id ? "· Fødselsnummer bekreftet" : ""} · {fmtDate(v.verified_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      {kyc.decision_note && <p className="text-sm text-muted-foreground mb-4">Notat: {kyc.decision_note}</p>}

      {kyc.status === "godkjent" && !showAnswers ? (
        <div className="mt-4 p-4 rounded-lg bg-secondary/50 border border-border flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="w-4 h-4 text-emerald-400" />
            KYC er godkjent. Kundens svar er skjult og arkivert som PDF under fanen «Dokumenter».
          </div>
          <Button size="sm" variant="secondary" data-testid="show-answers-btn" onClick={() => setShowAnswers(true)}>Vis svar</Button>
        </div>
      ) : (
        <>
          {kyc.status === "godkjent" && (
            <div className="flex justify-end mt-4">
              <Button size="sm" variant="ghost" data-testid="hide-answers-btn" onClick={() => setShowAnswers(false)}>Skjul svar</Button>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 mt-4 mb-2">
            <h4 className="font-medium">Svar fra kunde</h4>
            {!editAnswers && (
              <Button size="sm" variant="secondary" data-testid="kyc-edit-answers-btn" onClick={() => setEditAnswers(true)}>
                <Pencil className="w-3.5 h-3.5 mr-1" /> {Object.keys(kyc.answers || {}).length === 0 ? "Fyll ut svar internt" : "Rediger svar"}
              </Button>
            )}
          </div>
          {editAnswers ? (
            <KycAnswersEditor questions={questions} initial={kyc.answers || {}} onSave={saveAnswers} onCancel={() => setEditAnswers(false)} saving={savingAnswers} />
          ) : Object.keys(kyc.answers || {}).length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen KYC-svar registrert ennå. Send «KYC-invitasjon» til kunden, eller klikk «Fyll ut svar internt» for å registrere svarene på vegne av kunden.</p>
          ) : (
            <div className="space-y-2">
              {questions.map((q) => kyc.answers[q.id] != null && kyc.answers[q.id] !== "" && (
                <div key={q.id} className="text-sm"><span className="text-muted-foreground">{q.label}: </span>{String(kyc.answers[q.id])}</div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-6 mb-2 gap-2">
            <h4 className="font-medium">Styre og roller (Brønnøysund)</h4>
            <Button size="sm" variant="secondary" data-testid="fetch-board-btn" onClick={fetchBoard} disabled={fetchingBoard} className="text-xs h-8">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${fetchingBoard ? "animate-spin" : ""}`} /> {fetchingBoard ? "Henter…" : "Hent fra Brønnøysund"}
            </Button>
          </div>
          {(kyc.board_members || []).length === 0 ? <p className="text-sm text-muted-foreground">Ingen registrert. Klikk «Hent fra Brønnøysund».</p> : (
            <div className="space-y-2">
              {kyc.board_members.map((o, i) => {
                const validSsn = o.ssn && /^\d{11}$/.test(String(o.ssn).replace(/\D/g, ""));
                const isContact = !!o.contact_id;
                return (
                  <div key={o._key || `board-${i}`} className="p-3 rounded-lg bg-secondary/50 border border-border" data-testid={`board-member-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium">{o.name || "—"}</p>
                        <p className="text-xs text-muted-foreground">{[o.role, o.birth_date && `født ${o.birth_date}`, o.ssn && `f.nr ${o.ssn}`].filter(Boolean).join(" · ")}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {validSsn && (
                          <Button size="sm" variant="secondary" data-testid={`kyc-verify-krr-board_members-${i}`} onClick={() => verifyKrr("board_members", i)} disabled={verifying === `board_members-${i}`} className="text-xs h-8">
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {verifying === `board_members-${i}` ? "Henter…" : "Verifiser i KRR"}
                          </Button>
                        )}
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs mt-2 cursor-pointer" data-testid={`board-add-contact-${i}`}>
                      <Checkbox checked={isContact} disabled={togglingContact === i} onCheckedChange={(v) => toggleContact(i, !!v)} />
                      Legg til som kontaktperson for selskapet{isContact && <span className="text-emerald-400">· lagt til</span>}
                    </label>
                    <KrrBox krr={o.krr} testid={`kyc-krr-info-board_members-${i}`} />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className="mt-6 pt-4 border-t border-border" data-testid="rrr-section">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h4 className="font-medium">Reelle rettighetshavere</h4>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" data-testid="fetch-rrr-btn" onClick={fetchRrr} disabled={fetchingRrr} className="text-xs h-8">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${fetchingRrr ? "animate-spin" : ""}`} /> {fetchingRrr ? "Henter…" : "Hent fra Proff (RRR)"}
            </Button>
            <Button size="sm" variant="secondary" data-testid="screen-owners-btn" onClick={screenOwners} disabled={screeningOwners} className="text-xs h-8">
              <ShieldCheck className={`w-3.5 h-3.5 mr-1 ${screeningOwners ? "animate-spin" : ""}`} /> {screeningOwners ? "Sjekker…" : "Kjør PEP/sanksjonssjekk"}
            </Button>
            <Button size="sm" variant="secondary" data-testid="generate-kyc-pdf-btn" onClick={genKycPdf} disabled={genPdf} className="text-xs h-8">
              <FileText className="w-3.5 h-3.5 mr-1" /> {genPdf ? "Genererer…" : "Generer KYC-rapport (PDF)"}
            </Button>
          </div>
        </div>
        <KycPersonList group="beneficial_owners" people={kyc.beneficial_owners} verifying={verifying} onVerify={verifyKrr}
          meta={(o) => [o.share != null && o.share !== "" && `${o.share}%`, o.role, o.citizenship && o.citizenship !== "NO" && o.citizenship, o.source === "proff" && "Kilde: Proff", o.source === "rrr" && "Brønnøysund RRR", o.is_rrr && o.rrr_basis && o.rrr_basis.length && `RRR: ${o.rrr_basis.join(" + ")}`].filter(Boolean).join(" · ")} />
        <p className="text-xs text-muted-foreground/70 mt-1">«Reell rettighetshaver» markeres når Proff har flagget personen, eller ved eierandel over 25 % (hvitvaskingsloven § 14).</p>
        {kyc.rrr_checked_at && <p className="text-xs text-muted-foreground/70 mt-1">RRR sist hentet {fmtDate(kyc.rrr_checked_at)}{kyc.rrr_checked_by ? ` av ${kyc.rrr_checked_by}` : ""}</p>}

        <OwnershipTree customerId={c.id} />
      </div>
    </Card>
  );
}

function RisikoTab({ c, reload }) {
  const [factors, setFactors] = useState([]);
  const [risk, setRisk] = useState(null);
  const [ai, setAi] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportPdfBusy, setReportPdfBusy] = useState(false);
  useEffect(() => {
    api.get("/risk/factors").then((r) => {
      api.get(`/customers/${c.id}/risk`).then((rr) => {
        const existing = rr.data;
        setRisk(existing);
        setAi(existing?.ai_draft || "");
        setReport(existing?.compliance_report || "");
        const base = r.data.map((f) => {
          const e = existing?.factors?.find((x) => x.id === f.id);
          return { ...f, score: e?.score ?? 0 };
        });
        setFactors(base);
      });
    });
  }, [c.id]);
  const total = factors.reduce((s, f) => s + Number(f.score || 0), 0);
  const genAi = async () => {
    setBusy(true); setAi("");
    try { await streamGeneration(`/customers/${c.id}/risk/ai-draft`, setAi); toast.success("AI-utkast generert"); }
    catch (e) { toast.error(e.message || "Noe gikk galt"); } finally { setBusy(false); }
  };
  const save = async (approve) => {
    const { data } = await api.put(`/customers/${c.id}/risk`, { factors, ai_draft: ai, approved: approve });
    setRisk(data); toast.success(approve ? "Risikovurdering godkjent" : "Lagret"); reload();
  };
  const genReport = async () => {
    setReportBusy(true); setReport("");
    try { await streamGeneration(`/customers/${c.id}/risk/compliance-report`, setReport); toast.success("Kundekontrollrapport generert"); }
    catch (e) { toast.error(e.message || "Noe gikk galt"); } finally { setReportBusy(false); }
  };
  const saveReport = async () => {
    try { await api.put(`/customers/${c.id}/risk/compliance-report`, { compliance_report: report }); toast.success("Rapport lagret"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const genReportPdf = async () => {
    setReportPdfBusy(true);
    try { const { data } = await api.post(`/customers/${c.id}/risk/compliance-report/pdf`); toast.success(`PDF lagret: ${data.name} — ligger under Dokumenter`); }
    catch (e) { toast.error(apiError(e)); } finally { setReportPdfBusy(false); }
  };
  return (
    <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Risikofaktorer" action={<span className="text-sm text-muted-foreground">Score {total}/{factors.length * 4}</span>}>
        <div className="space-y-3">
          {factors.map((f, i) => (
            <div key={f.id} className="flex items-center justify-between gap-3">
              <span className="text-sm">{f.label}</span>
              <Select value={String(f.score)} onValueChange={(v) => { const cp = [...factors]; cp[i] = { ...f, score: Number(v) }; setFactors(cp); }}>
                <SelectTrigger className="w-32 bg-secondary border-border h-8" data-testid={`risk-factor-${f.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {[["0", "Ingen"], ["1", "Lav"], ["2", "Middels"], ["3", "Høy"], ["4", "Svært høy"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        {risk && <div className="mt-4 pt-4 border-t border-border flex items-center gap-2"><span className="label-caps">Nivå:</span><StatusBadge map={RISK} value={risk.level} /> {risk.approved && <span className="text-xs text-emerald-400">✓ Godkjent av {risk.approved_by}</span>}</div>}
        <div className="flex gap-2 mt-4">
          <Button size="sm" variant="secondary" data-testid="risk-save" onClick={() => save(false)}>Lagre</Button>
          <Button size="sm" data-testid="risk-approve" onClick={() => save(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" /> Godkjenn vurdering</Button>
        </div>
      </Card>
      <Card title="AI-assistert risikoutkast" action={<Button size="sm" variant="secondary" data-testid="ai-risk-btn" onClick={genAi} disabled={busy}><Sparkles className="w-4 h-4 mr-1" /> {busy ? "Genererer…" : "Generer utkast"}</Button>}>
        <p className="text-xs text-muted-foreground mb-3">Kun forslag — skal alltid kvalitetssikres og godkjennes av et menneske.</p>
        <Textarea value={ai} onChange={(e) => setAi(e.target.value)} rows={14} className="bg-secondary border-border text-sm" placeholder="Ingen AI-utkast ennå. Klikk «Generer utkast»." data-testid="ai-risk-text" />
      </Card>
    </div>
      <Card title="Kundekontrollrapport (AI)" action={
        <Button size="sm" variant="secondary" data-testid="gen-compliance-report-btn" onClick={genReport} disabled={reportBusy}><Sparkles className="w-4 h-4 mr-1" /> {reportBusy ? "Genererer…" : "Generer rapport"}</Button>
      }>
        <p className="text-xs text-muted-foreground mb-3">Samlet rapport basert på KYC, eierstruktur, reelle rettighetshavere, PEP/sanksjon og risikovurdering. Kun forslag – redigér og kvalitetssikre før bruk.</p>
        <Textarea value={report} onChange={(e) => setReport(e.target.value)} rows={18} className="bg-secondary border-border text-sm" placeholder="Ingen rapport ennå. Klikk «Generer rapport»." data-testid="compliance-report-text" />
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" variant="secondary" data-testid="save-compliance-report-btn" onClick={saveReport} disabled={!report}>Lagre endringer</Button>
          <Button size="sm" data-testid="compliance-report-pdf-btn" onClick={genReportPdf} disabled={!report || reportPdfBusy} className="bg-white text-black hover:bg-white/90"><FileText className="w-4 h-4 mr-1" /> {reportPdfBusy ? "Lager PDF…" : "Generer PDF (lagre under Dokumenter)"}</Button>
        </div>
      </Card>
    </div>
  );
}

const AGREEMENT_TYPES = [["regnskap", "Regnskap"], ["revisjon", "Revisjon"], ["juridisk", "Juridisk rådgivning"], ["utleie", "Utleie"]];
const AGREEMENT_TYPE_LABEL = Object.fromEntries(AGREEMENT_TYPES);
const DEPT_TO_TYPE = { Regnskap: "regnskap", Revisjon: "revisjon", Juss: "juridisk", Utleie: "utleie" };

function GenerateAgreementDialog({ c, onGenerated }) {
  const initType = DEPT_TO_TYPE[(c.departments && c.departments[0]) || c.department] || "regnskap";
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(initType);
  const [busy, setBusy] = useState(false);
  const [u, setU] = useState({ resource_name: "", period_from: "", period_to: "", notice_period: "1 måned", price: 22500 });
  const [r, setR] = useState({ associate_rate: "", deadline_plan: "", deadline_docs: "", deadline_final: "", auditor_contact: "", accounting_framework: "regnskapsloven", framework_other: "", special_terms: "", other_services: "" });
  useEffect(() => { if (open) setType(initType); }, [open, initType]);
  const submit = async () => {
    setBusy(true);
    try {
      let options = {};
      if (type === "utleie") options = { ...u, price: Number(u.price) || 22500 };
      else if (type === "revisjon") options = { ...r, associate_rate: r.associate_rate === "" ? null : Number(r.associate_rate) };
      await api.post(`/customers/${c.id}/agreements/generate`, { agreement_type: type, options });
      toast.success(`Avtale generert (${AGREEMENT_TYPE_LABEL[type]})`);
      setOpen(false);
      onGenerated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="generate-agreement-btn" className="bg-white text-black hover:bg-white/90"><FileText className="w-4 h-4 mr-1" /> Generer avtale</Button>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle>Generer avtale</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-caps">Avtaletype (område)</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="agreement-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {AGREEMENT_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Forhåndsvalgt ut fra kundens avdeling – kan endres.</p>
          </div>
          {type === "utleie" && (
            <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3">
              <div><Label className="label-caps">Navngitt innleid ressurs</Label><Input value={u.resource_name} onChange={(e) => setU({ ...u, resource_name: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="utleie-resource" placeholder="F.eks. Kari Nordmann" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="label-caps">Periode fra</Label><Input type="date" value={u.period_from} onChange={(e) => setU({ ...u, period_from: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="utleie-from" /></div>
                <div><Label className="label-caps">Periode til</Label><Input type="date" value={u.period_to} onChange={(e) => setU({ ...u, period_to: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="utleie-to" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="label-caps">Oppsigelsestid</Label><Input value={u.notice_period} onChange={(e) => setU({ ...u, notice_period: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="utleie-notice" /></div>
                <div><Label className="label-caps">Fast pris/mnd (kr)</Label><Input type="number" value={u.price} onChange={(e) => setU({ ...u, price: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="utleie-price" /></div>
              </div>
            </div>
          )}
          {type === "revisjon" && (
            <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3">
              <p className="text-xs text-muted-foreground">Fyll inn så «[Fyll inn]» forsvinner fra PDF-en. Alt er valgfritt.</p>
              <div><Label className="label-caps">Medarbeidersats (kr/time)</Label><Input type="number" value={r.associate_rate} onChange={(e) => setR({ ...r, associate_rate: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="rev-associate-rate" placeholder="F.eks. 1100" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="label-caps">Frist plan</Label><Input value={r.deadline_plan} onChange={(e) => setR({ ...r, deadline_plan: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="rev-deadline-plan" placeholder="15.09" /></div>
                <div><Label className="label-caps">Frist dok.</Label><Input value={r.deadline_docs} onChange={(e) => setR({ ...r, deadline_docs: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="rev-deadline-docs" placeholder="15.05" /></div>
                <div><Label className="label-caps">Frist signert</Label><Input value={r.deadline_final} onChange={(e) => setR({ ...r, deadline_final: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="rev-deadline-final" placeholder="30.06" /></div>
              </div>
              <div><Label className="label-caps">Revisors kontaktinfo (e-post/telefon)</Label><Input value={r.auditor_contact} onChange={(e) => setR({ ...r, auditor_contact: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="rev-auditor-contact" placeholder="post@… · 90 00 00 00" /></div>
              <div>
                <Label className="label-caps">Regnskapsregelverk</Label>
                <Select value={r.accounting_framework} onValueChange={(v) => setR({ ...r, accounting_framework: v })}>
                  <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="rev-framework"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="regnskapsloven">Regnskapsloven og god regnskapsskikk</SelectItem>
                    <SelectItem value="forenklet_ifrs">Forenklet IFRS</SelectItem>
                    <SelectItem value="annet">Annet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {r.accounting_framework === "annet" && <div><Label className="label-caps">Annet regelverk</Label><Input value={r.framework_other} onChange={(e) => setR({ ...r, framework_other: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="rev-framework-other" /></div>}
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="label-caps">Særskilte forutsetninger</Label><Input value={r.special_terms} onChange={(e) => setR({ ...r, special_terms: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="rev-special" placeholder="Ingen" /></div>
                <div><Label className="label-caps">Andre avtalte tjenester</Label><Input value={r.other_services} onChange={(e) => setR({ ...r, other_services: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="rev-other" placeholder="Ingen – avtales særskilt" /></div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid="agreement-generate-confirm" className="bg-white text-black hover:bg-white/90">{busy ? "Genererer…" : "Generer"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AvtalerTab({ c, reload }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`/customers/${c.id}/agreements`).then((r) => setRows(r.data)), [c.id]);
  useEffect(() => { load(); }, [load]);
  const approve = async (aid) => { await api.post(`/agreements/${aid}/approve`); toast.success("Godkjent internt"); load(); reload(); };
  const countersign = async (aid) => {
    try {
      const { data } = await api.post(`/agreements/${aid}/countersign`);
      toast.success(data.completed ? "Avtalen er ferdig signert – kunde aktivert!" : "Signert som oppdragsansvarlig – venter på kundens signatur");
      load(); reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const send = async (aid) => {
    try {
      const { data } = await api.post(`/agreements/${aid}/send-signing`, {});
      const n = data.required_signers || (data.sent_to || []).length;
      toast.success(`Avtale sendt til ${n} signatar${n === 1 ? "" : "er"} for digital signering`, { description: (data.sent_to || []).join(", "), duration: 10000 });
      load(); reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openDoc = async (path, type) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (aid) => {
    if (!window.confirm("Slette denne oppdragsavtalen? Dette kan ikke angres.")) return;
    try {
      await api.delete(`/agreements/${aid}`);
      toast.success("Avtale slettet");
      load(); reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card title="Oppdragsavtaler" action={<GenerateAgreementDialog c={c} onGenerated={() => { load(); reload(); }} />}>
      <div className="space-y-3">
        {rows.map((a) => (
          <div key={a.id} data-testid={`agreement-${a.id}`} className="p-4 rounded-lg bg-secondary/50 border border-border">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium text-sm">{a.agreement_id} <span className="text-muted-foreground">· v{a.version}</span>{a.agreement_type && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground" data-testid={`agreement-type-${a.id}`}>{AGREEMENT_TYPE_LABEL[a.agreement_type] || a.agreement_type}</span>}</p>
                <p className="text-xs text-muted-foreground mt-0.5">MRR {fmtNOK(a.mrr)} · {fmtDate(a.created_at)} · <StatusBadge map={SIGN_STATUS} value={a.sign_status} /></p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => openDoc(`/agreements/${a.id}/preview`, "text/html")}>Forhåndsvis</Button>
                <Button size="sm" variant="secondary" onClick={() => openDoc(`/agreements/${a.id}/pdf`, "application/pdf")}><Download className="w-4 h-4 mr-1" /> PDF</Button>
                {!a.internally_approved && <Button size="sm" data-testid={`approve-${a.id}`} onClick={() => approve(a.id)} className="bg-white text-black hover:bg-white/90"><Check className="w-4 h-4 mr-1" /> Godkjenn</Button>}
                {a.internally_approved && a.sign_status === "ikke_opprettet" && <Button size="sm" data-testid={`send-${a.id}`} onClick={() => send(a.id)} className="bg-violet-600 hover:bg-violet-700 text-white"><Send className="w-4 h-4 mr-1" /> Send til digital signering</Button>}
                {["sendt", "levert", "apnet", "venter_motsignatur", "delvis_signert"].includes(a.sign_status) && !a.customer_signed && !a.signed && <Button size="sm" variant="secondary" data-testid={`resend-${a.id}`} onClick={() => send(a.id)}><Send className="w-4 h-4 mr-1" /> Send lenke på nytt</Button>}
                {a.internally_approved && !a.manager_signed && !a.signed && <Button size="sm" data-testid={`countersign-${a.id}`} onClick={() => countersign(a.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Pencil className="w-4 h-4 mr-1" /> Signer som oppdragsansvarlig</Button>}
                {!a.signed && !a.customer_signed && !a.manager_signed && !(a.required_signers || []).some((s) => s.signed) && <Button size="sm" variant="destructive" data-testid={`delete-agreement-${a.id}`} onClick={() => del(a.id)}><Trash2 className="w-4 h-4 mr-1" /> Slett</Button>}
              </div>
            </div>
            {(a.customer_signed || a.manager_signed || a.internally_approved || (a.required_signers || []).length > 0) && (
              <div className="mt-3 pt-3 border-t border-border text-xs" data-testid={`sign-progress-${a.id}`}>
                {(a.required_signers || []).length > 1 && (
                  <p className="text-amber-300 mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Krever signatur fra {a.required_signers.length} parter – alle må signere.</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {(a.required_signers || []).length > 0 ? (
                    a.required_signers.map((s) => (
                      <span key={s.contact_id || s.token} className={s.signed ? "text-emerald-400" : "text-muted-foreground"}>{s.signed ? "✓" : "○"} {s.name || s.email}{s.signed && s.signed_at ? ` (${fmtDate(s.signed_at)})` : ""}</span>
                    ))
                  ) : (
                    <span className={a.customer_signed ? "text-emerald-400" : "text-muted-foreground"}>{a.customer_signed ? "✓" : "○"} Kunde{a.customer_signer_name ? `: ${a.customer_signer_name}` : ""}</span>
                  )}
                  <span className={a.manager_signed ? "text-emerald-400" : "text-muted-foreground"}>{a.manager_signed ? "✓" : "○"} Oppdragsansvarlig{a.manager_signer_name ? `: ${a.manager_signer_name}` : ""}</span>
                  {a.signed && <span className="text-emerald-400 font-medium">· Ferdig signert</span>}
                </div>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Ingen avtaler generert. Klikk «Generer avtale».</p>}
      </div>
    </Card>
  );
}

function DokumenterTab({ c }) {
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("ovrige");
  const load = useCallback(() => api.get(`/customers/${c.id}/documents`).then((r) => setDocs(r.data)), [c.id]);
  useEffect(() => { load(); api.get("/document-categories").then((r) => setCats(r.data)); }, [load]);
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("category", cat);
    await api.post(`/customers/${c.id}/documents`, fd);
    toast.success("Lastet opp"); load();
  };
  const download = async (d) => {
    const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = d.name; a.click();
  };
  return (
    <Card title="Dokumentarkiv" action={
      <div className="flex items-center gap-2">
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-40 bg-secondary border-border h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover border-border">{cats.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select>
        <Button size="sm" asChild className="bg-white text-black hover:bg-white/90"><label data-testid="upload-doc-btn"><Upload className="w-4 h-4 mr-1" /> Last opp<input type="file" className="hidden" onChange={upload} /></label></Button>
      </div>
    }>
      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 border border-border">
            <div className="flex items-center gap-3"><FileText className="w-4 h-4 text-muted-foreground" /><div><p className="text-sm font-medium">{d.name}</p><p className="text-xs text-muted-foreground">{d.category} · {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by} · {fmtDate(d.uploaded_at)}</p></div></div>
            <button onClick={() => download(d)} className="text-muted-foreground hover:text-foreground p-2"><Download className="w-4 h-4" /></button>
          </div>
        ))}
        {docs.length === 0 && <p className="text-sm text-muted-foreground">Ingen dokumenter.</p>}
      </div>
    </Card>
  );
}

function OppgaverTab({ c }) {
  const [rows, setRows] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const load = useCallback(() => api.get(`/tasks?customer_id=${c.id}`).then((r) => setRows(r.data)), [c.id]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (t) => { await api.put(`/tasks/${t.id}`, { status: t.status === "ferdig" ? "apen" : "ferdig" }); load(); };
  const locked = { id: c.id, legal_name: c.legal_name };
  return (
    <>
      <Card title="Oppgaver" action={<Button size="sm" data-testid="add-task-btn" onClick={() => setNewOpen(true)} className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Ny oppgave</Button>}>
        <div className="space-y-2">
          {rows.map((t) => (
            <div key={t.id} data-testid={`task-${t.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-border">
              <Checkbox checked={t.status === "ferdig"} onCheckedChange={() => toggle(t)} data-testid={`task-check-${t.id}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm flex items-center gap-2 ${t.status === "ferdig" ? "line-through text-muted-foreground" : ""}`}>
                  {t.title}
                  {t.recurrence && <span className="inline-flex items-center gap-1 text-[11px] text-blue-400 shrink-0"><Repeat className="w-3 h-3" />{RECURRENCE_LABELS[t.recurrence] || t.recurrence}</span>}
                </p>
                {t.description && <p className="text-xs text-muted-foreground/80 mt-0.5">{t.description}</p>}
                <p className="text-xs text-muted-foreground">{t.due_date && <>Frist {fmtDate(t.due_date)} · </>}{t.assignee_name}{t.priority && t.priority !== "normal" ? ` · ${t.priority}` : ""}</p>
              </div>
              <button onClick={() => setEditTask(t)} data-testid={`task-edit-${t.id}`} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent" title="Rediger"><Pencil className="w-4 h-4" /></button>
            </div>
          ))}
          {rows.length === 0 && <p className="text-sm text-muted-foreground">Ingen oppgaver.</p>}
        </div>
      </Card>
      <TaskFormDialog open={newOpen} onOpenChange={setNewOpen} task={null} lockedCustomer={locked} onSaved={load} />
      <TaskFormDialog open={!!editTask} onOpenChange={(o) => !o && setEditTask(null)} task={editTask} lockedCustomer={locked} onSaved={load} />
      <YearEndCustomerSection customerId={c.id} />
    </>
  );
}

function AktiviteterTab({ c }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/customers/${c.id}/activities`).then((r) => setRows(r.data)); }, [c.id]);
  return (
    <Card title="Aktivitetslogg">
      <div className="space-y-3">
        {rows.map((a) => (
          <div key={a.id} className="flex gap-3 text-sm border-l-2 border-border pl-3">
            <div><p>{a.title}</p><p className="text-xs text-muted-foreground">{a.type} · {a.user_name} · {fmtDate(a.timestamp)}</p></div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Ingen aktiviteter.</p>}
      </div>
    </Card>
  );
}

function NotaterTab({ c }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(() => api.get(`/customers/${c.id}/notes`).then((r) => setRows(r.data)), [c.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!text) return; await api.post(`/customers/${c.id}/notes`, { text }); setText(""); load(); };
  return (
    <Card title="Interne notater">
      <div className="flex gap-2 mb-4">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Skriv et internt notat…" className="bg-secondary border-border" rows={2} data-testid="note-input" />
        <Button onClick={add} data-testid="add-note-btn" className="bg-white text-black hover:bg-white/90 self-start">Lagre</Button>
      </div>
      <div className="space-y-2">
        {rows.map((n) => (
          <div key={n.id} className="p-3 rounded-lg bg-secondary/50 border border-border">
            <p className="text-sm">{n.text}</p>
            <p className="text-xs text-muted-foreground mt-1">{n.user_name} · {fmtDate(n.timestamp)}</p>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Ingen notater.</p>}
      </div>
    </Card>
  );
}

function HistorikkTab({ c }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/customers/${c.id}/audit-log`).then((r) => setRows(r.data)); }, [c.id]);
  return (
    <Card title="Revisjonslogg">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left">{["Tidspunkt", "Bruker", "Handling", "Objekt"].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.id} className="border-b border-border/50">
              <td className="px-3 py-2 text-muted-foreground text-xs">{fmtDate(l.timestamp)}</td>
              <td className="px-3 py-2">{l.user_name}</td>
              <td className="px-3 py-2">{l.action}</td>
              <td className="px-3 py-2 text-muted-foreground">{l.entity_type}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className="text-sm text-muted-foreground mt-2">Ingen hendelser.</p>}
    </Card>
  );
}
